import React, { useState, useEffect } from "react";
import { ToastContainer, toast } from "react-toastify"; // Import toast functionality
import CountUp from "react-countup";
import "react-toastify/dist/ReactToastify.css"; // Import default styles
import {
  Brain,
  ChevronDown,
  Facebook,
  Instagram,
  Linkedin,
  Twitter,
  Youtube,
  Mail,
  Settings,
  Users,
  Link2,
  Cog,
  FileText,
} from "lucide-react";

// Import our components
import WorkflowBuilder from "./components/interactive/WorkflowBuilder";
import DataSimulation from "./components/interactive/DataSimulation";
import BusinessImpact from "./components/sections/BusinessImpact";
import FloatingChat from "./components/interactive/FloatingChat";

declare const Calendly: any;

function App() {
  // State for metrics
  const [metrics, setMetrics] = useState({
    automatedTasks: 0,
    timeOptimized: 0,
    costsReduced: 0,
    errorReduction: 0,
  });

  // State for active section
  const [activeSection, setActiveSection] = useState("hero");

  // Handle section visibility for navigation highlighting
  const handleSectionVisible = (section: string) => {
    setActiveSection(section);
  };

  // Update metrics from components
  const updateMetrics = (newMetrics: Partial<typeof metrics>) => {
    setMetrics((prev) => ({
      ...prev,
      ...newMetrics,
    }));
  };

  // Add a ref to your root div
  useEffect(() => {
    const handleCalendlyEvent = (e: MessageEvent) => {
      console.log("Calendly event received:", e.data); // Debugging log
      if (e.data?.event === "calendly.event_scheduled") {
        Calendly.closePopupWidget();

        // ✅ Reset the form here
        setFormData({
          name: "",
          email: "",
          service: "",
        });

        toast.success(
          <div>
            <p className="text-lg font-medium">
              Thanks for scheduling your call!
            </p>
            <p className="text-sm text-gray-400">
              We'll connect soon and help elevate your business.
            </p>
          </div>,
          {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            style: {
              backgroundColor: "#0A0A1B",
              color: "white",
              borderColor: "#6A5ACD",
            },
            icon: <span className="text-purple-400 text-xl">✓</span>,
          }
        );
      }
    };

    // Add the event listener
    window.addEventListener("message", handleCalendlyEvent);

    // Cleanup the event listener on component unmount
    return () => {
      window.removeEventListener("message", handleCalendlyEvent);
    };
  }, []);

  const [email, setEmail] = useState("");
  // Handle email input change
  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
  };

  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    service: "",
  });

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };
  const handleSubscribe = () => {
    if (!email) {
      toast.error("Please enter a valid email!", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      return;
    }

    toast.success(
      <div>
        <p className="text-lg font-medium">Thanks for subscribing!</p>
        <p className="text-sm text-gray-400">
          We'll keep you updated with the latest AI insights.
        </p>
      </div>,
      {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        style: {
          backgroundColor: "#0A0A1B", // Dark background
          color: "white", // Text color
          borderColor: "#6A5ACD", // Border color (matching your branding)
        },
        icon: <span className="text-purple-400 text-xl">✓</span>,
      }
    );

    setEmail("");
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Validate form data
    if (!formData.name || !formData.email || !formData.service) {
      toast.error("Please fill out all fields!", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      return;
    }

    // Open Calendly popup with pre-filled data
    Calendly.initPopupWidget({
      url: `https://calendly.com/automate-innovateaitechnologies/25-minute-intro-call`,
      prefill: {
        name: formData.name,
        email: formData.email,
        custom_service: formData.service,
      },
      utm: {}, // Add UTM parameters if needed
      onLoad: () => {
        console.log("Calendly popup loaded");
      },
      onClose: () => {
        console.log("Calendly popup closed");
      },
      // onEventScheduled: (e: any) => {
      //   // Show success toast only when an event is scheduled
      //   toast.success("Thank you! Your call has been scheduled successfully.", {
      //     position: "top-right",
      //     autoClose: 5000,
      //     hideProgressBar: false,
      //     closeOnClick: true,
      //     pauseOnHover: true,
      //     draggable: true,
      //   });
      // },
    });
  };

  const services = [
    {
      icon: <Settings className="w-12 h-12 text-purple-400" />,
      title: "Project Management Systems",
      description:
        "Bespoke project management systems used by 8-figure agencies & SaaS companies.",
    },
    {
      icon: <Users className="w-12 h-12 text-purple-400" />,
      title: "Custom CRM builds",
      description:
        "Sales systems to track, iterate, and scale growth just like an 8-figure company.",
    },
    {
      icon: <Users className="w-12 h-12 text-purple-400" />,
      title: "Hiring systems",
      description:
        "Automated processes that source, contact, and evaluate candidates for you.",
    },
    {
      icon: <Link2 className="w-12 h-12 text-purple-400" />,
      title: "Lead generation",
      description:
        "Scalable, affordable outbound & marketing systems to grow your company on autopilot.",
    },
    {
      icon: <Cog className="w-12 h-12 text-purple-400" />,
      title: "Automated Service Fulfillment",
      description:
        "AI that automates key steps in your fulfillment process to reduce payroll.",
    },
    {
      icon: <FileText className="w-12 h-12 text-purple-400" />,
      title: "SOPs & Consultation",
      description:
        "We'll help clarify your offer, show you what 8-figure companies are doing, and build you a library of SOPs.",
    },
  ];

  const faqs = [
    {
      question: "What are the benefits of business process automation?",
      answer:
        "Leveraging technology to handle repetitive tasks that previously required human involvement. This approach not only cuts down on the time and costs associated with these tasks but also reduces mistakes and allows your team to focus on more value-added activities.",
    },
    {
      question: "Can your solutions be integrated with existing systems?",
      answer:
        "Absolutely, our services are custom-designed to fit seamlessly with your specific needs and the tools you already use. Such as your CRM (Hubspot, Monday, etc.) or project management system (ClickUp, Notion, Asana, etc.)",
    },
    {
      question: "What is a custom CRM and how can it benefit my business?",
      answer:
        "A custom CRM is a platform tailored specifically to meet your business's unique customer relationship management needs. It enables you to track customer interactions, automate marketing and sales tasks, and access detailed analytics to make better-informed decisions.",
    },
  ];

  const caseStudies = [
    {
      category: "Supply Chain",
      title:
        "Streamlined Supplier and Buyer Identification in Worldwide Food Trading",
      description:
        "As YMF Global expanded, they needed a streamlined approach to managing trade data. We implemented a web scraping system that gathered supplier and buyer data from multiple online sources, helping them quickly capitalize on market opportunities.",
      image:
        "https://images.unsplash.com/photo-1494412574643-ff11b0a5c1c3?auto=format&fit=crop&q=80&w=800",
    },
    {
      category: "Executive Hiring",
      title: "Enhancing Visibility and Efficiency in Executive Recruitment",
      description:
        "Recruitment is a service-driven industry, where efficiency and timely communication are essential. We built a centralized dashboard that streamlined search tracking, automated notifications for delays, and integrated client feedback through a Net Promoter Score (NPS) survey system.",
      image:
        "https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&q=80&w=800",
    },
  ];

  return (
    <div className="min-h-screen bg-[#0A0A1B] text-white">
      {/* Navigation */}
      <nav className="container mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Brain className="w-8 h-8 text-purple-500" />
            <span className="text-xl font-bold">Innovate AI Technologies</span>
          </div>
          <div className="hidden md:flex space-x-8">
            <a
              href="#services"
              className={`hover:text-purple-400 ${
                activeSection === "services" ? "text-purple-400" : ""
              }`}
            >
              Services
            </a>
            <a
              href="#workflow"
              className={`hover:text-purple-400 ${
                activeSection === "workflow" ? "text-purple-400" : ""
              }`}
            >
              Workflow Builder
            </a>
            <a
              href="#impact"
              className={`hover:text-purple-400 ${
                activeSection === "impact" ? "text-purple-400" : ""
              }`}
            >
              Business Impact
            </a>
            <a
              href="#book-call"
              className={`hover:text-purple-400 ${
                activeSection === "contact" ? "text-purple-400" : ""
              }`}
            >
              Contact
            </a>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="container mx-auto px-6 py-20">
        <div className="text-center">
          <div className="inline-block px-4 py-2 bg-purple-900/30 rounded-full mb-4">
            <span className="text-purple-400  text-sm align-middle">
              ⭐ AI Automation
            </span>
          </div>

          <h1 className="text-5xl md:text-6xl font-bold mb-6">
            Let AI Do the Heavy Lifting,
            <br />
            <span className="text-purple-400">
              So You Can Elevate Your Business
            </span>
          </h1>
          <p className="text-xl text-gray-400 mb-8 relative">
            Optimize your offer, automate leads, remove sales bottlenecks, and
            scale to 8 figures
          </p>
          <a
            href="#book-call"
            className="inline-block bg-purple-600 hover:bg-purple-700 text-white font-semibold px-8 py-4 rounded-lg transition-all duration-300 transform hover:scale-105 hover:shadow-lg"
          >
            Book Your Call
          </a>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="container mx-auto px-6 py-20">
        <div className="text-center mb-16">
          <div className="inline-block px-6 py-2 bg-gray-800 rounded-full mb-4">
            SERVICES
          </div>
          <h2 className="text-4xl font-bold">Solutions that matter</h2>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, index) => (
            <div
              key={index}
              className="bg-gray-900/50 p-8 rounded-2xl text-center"
            >
              <div className="flex justify-center mb-6">{service.icon}</div>
              <h3 className="text-xl font-bold mb-4">{service.title}</h3>
              <p className="text-gray-400">{service.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Workflow Builder Section */}
      <section id="workflow" className="py-16">
        <WorkflowBuilder onMetricsUpdate={updateMetrics} />
      </section>

      {/* Business Impact Section */}
      <BusinessImpact
        metrics={{
          automatedTasks: metrics.automatedTasks,
          timeOptimized: metrics.timeOptimized,
          costsReduced: metrics.costsReduced,
        }}
        onSectionVisible={handleSectionVisible}
      />

      {/* Data Simulation Section */}
      <section id="data-simulation" className="py-16">
        <DataSimulation onMetricsUpdate={updateMetrics} />
      </section>

      {/* Integration Grid */}
      <section className="container mx-auto px-6 py-20">
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-8 items-center justify-items-center">
          {[
            { label: "$100K+", description: "Earned for clients" },
            { label: "400M+", description: "Hours saved" },
            { label: "95%", description: "Client satisfaction" },
            { label: "100+", description: "Projects completed" },
            { label: "24/7", description: "Support available" },
            { label: "AI-driven", description: "Automation solutions" },
          ].map((item, index) => (
            <div key={index} className="text-center">
              {/* Animated Label */}
              <div className="text-2xl font-bold text-purple-400">
                {item.label.includes("+") || item.label.includes("%") ? (
                  <CountUp
                    end={parseFloat(item.label.replace(/[^0-9.]/g, ""))}
                    suffix={item.label.match(/[+%]/)?.[0] || ""}
                    duration={2.5}
                    enableScrollSpy
                    scrollSpyOnce
                  />
                ) : (
                  item.label
                )}
              </div>
              {/* Description */}
              <div className="text-gray-400 text-sm">{item.description}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Booking Section */}
      {/* Form and Calendly Popup */}
      <section id="book-call" className="container mx-auto px-6 py-20">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold mb-4">
            Get started today with a 25min intro call
          </h2>
          <p className="text-gray-400">
            It's like a digital coffee (with no hard sell)
          </p>
        </div>
        <div className="max-w-3xl mx-auto bg-gray-900/50 p-8 rounded-2xl">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            {/* Image Column */}
            <div>
              <img
                src="https://images.unsplash.com/photo-1593642532744-d377ab507dc8?auto=format&fit=crop&q=80&w=800"
                alt="Laptop"
                className="rounded-lg object-cover w-full h-full"
              />
            </div>

            {/* Form Column */}
            <div>
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Name Field */}
                <div>
                  <label
                    htmlFor="name"
                    className="block text-sm font-medium text-gray-400 mb-1"
                  >
                    Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="w-full bg-gray-800 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                    required
                  />
                </div>

                {/* Email Field */}
                <div>
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium text-gray-400 mb-1"
                  >
                    Email
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full bg-gray-800 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                    required
                  />
                </div>

                {/* Service Field */}
                <div>
                  <label
                    htmlFor="service"
                    className="block text-sm font-medium text-gray-400 mb-1"
                  >
                    Select a Service
                  </label>
                  <div className="relative">
                    <select
                      id="service"
                      name="service"
                      value={formData.service}
                      onChange={handleInputChange}
                      className="w-full bg-gray-800 rounded-lg px-4 py-3 text-white appearance-none pr-10 focus:outline-none focus:ring-2 focus:ring-purple-500 cursor-pointer"
                      required
                    >
                      <option value="">Select a service</option>
                      {services.map((service, index) => (
                        <option key={index} value={service.title}>
                          {service.title}
                        </option>
                      ))}
                    </select>
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                      <ChevronDown className="h-5 w-5 text-gray-400" />
                    </div>
                  </div>
                </div>

                <button
                  type="submit"
                  className="w-full bg-purple-600 hover:bg-purple-700 text-white font-semibold px-6 py-3 rounded-lg transition-all duration-300 transform hover:scale-105 hover:shadow-lg"
                >
                  Book Your Call
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      <section className="container mx-auto px-6 py-20">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold mb-4">FAQs</h2>
          <p className="text-gray-400 max-w-3xl mx-auto">
            Common questions about our AI automation services and solutions
          </p>
        </div>

        <div className="max-w-3xl mx-auto">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="mb-4 border-b border-gray-800 pb-4 last:border-b-0 last:pb-0"
            >
              <button
                onClick={() => setOpenFaq(openFaq === index ? null : index)}
                className="flex justify-between items-center w-full text-left py-2 focus:outline-none"
              >
                <h3 className="text-lg font-semibold">{faq.question}</h3>
                <ChevronDown
                  className={`w-5 h-5 transform transition-transform ${
                    openFaq === index ? "rotate-180" : ""
                  }`}
                />
              </button>
              {openFaq === index && (
                <div className="mt-2 text-gray-400">{faq.answer}</div>
              )}
            </div>
          ))}
        </div>
      </section>

      <section className="container mx-auto px-6 py-20">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold mb-4">Stay Updated</h2>
          <p className="text-gray-400 max-w-xl mx-auto">
            Join our newsletter to stay updated with the latest AI automation
            trends, tips, and insights for your business.
          </p>
        </div>

        <div className="max-w-xl mx-auto bg-gray-900/50 p-8 rounded-2xl">
          <div className="flex flex-col md:flex-row space-y-4 md:space-y-0 md:space-x-4">
            <input
              type="email"
              value={email}
              onChange={handleEmailChange}
              placeholder="Your email address"
              className="flex-1 bg-gray-800 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
            <button
              onClick={handleSubscribe}
              className="bg-purple-600 hover:bg-purple-700 text-white font-semibold px-6 py-3 rounded-lg transition-all duration-300 transform hover:scale-105 hover:shadow-lg whitespace-nowrap"
            >
              Subscribe
            </button>
          </div>
        </div>
      </section>

      <footer className="bg-gray-900 py-12">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="md:col-span-2">
              <div className="flex items-center space-x-2 mb-4">
                <Brain className="w-8 h-8 text-purple-500" />
                <span className="text-xl font-bold">
                  Innovate AI Technologies
                </span>
              </div>
              <p className="text-gray-400 mb-4">
                Transforming businesses through intelligent automation and
                AI-powered solutions that streamline processes, reduce costs,
                and boost productivity.
              </p>
              <div className="flex space-x-4">
                <a
                  href="#"
                  className="text-gray-400 hover:text-purple-400 transition-colors"
                >
                  <Twitter className="w-5 h-5" />
                </a>
                <a
                  href="#"
                  className="text-gray-400 hover:text-purple-400 transition-colors"
                >
                  <Linkedin className="w-5 h-5" />
                </a>
                <a
                  href="#"
                  className="text-gray-400 hover:text-purple-400 transition-colors"
                >
                  <Facebook className="w-5 h-5" />
                </a>
                <a
                  href="#"
                  className="text-gray-400 hover:text-purple-400 transition-colors"
                >
                  <Instagram className="w-5 h-5" />
                </a>
                <a
                  href="#"
                  className="text-gray-400 hover:text-purple-400 transition-colors"
                >
                  <Youtube className="w-5 h-5" />
                </a>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-4">Quick Links</h3>
              <ul className="space-y-2">
                <li>
                  <a
                    href="#services"
                    className="text-gray-400 hover:text-white transition-colors"
                  >
                    Services
                  </a>
                </li>
                <li>
                  <a
                    href="#case-studies"
                    className="text-gray-400 hover:text-white transition-colors"
                  >
                    Case Studies
                  </a>
                </li>
                <li>
                  <a
                    href="#about"
                    className="text-gray-400 hover:text-white transition-colors"
                  >
                    About Us
                  </a>
                </li>
                <li>
                  <a
                    href="#book-call"
                    className="text-gray-400 hover:text-white transition-colors"
                  >
                    Book a Call
                  </a>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-4">Contact</h3>
              <ul className="space-y-2">
                <li className="flex items-start space-x-2">
                  <Mail className="w-5 h-5 text-purple-400 mt-0.5" />
                  <span className="text-gray-400">
                    automate@innovateaitechnologies.com
                  </span>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 mt-12 pt-8 text-center">
            <p className="text-gray-400">
              &copy; {new Date().getFullYear()} Innovate AI Technologies. All
              rights reserved.
            </p>
          </div>
        </div>
      </footer>

      {/* Add the floating chat component */}
      <FloatingChat
        onMetricsUpdate={(chatMetrics) => {
          // Optional: update any metrics from chat interactions
        }}
      />

      <ToastContainer />
    </div>
  );
}

export default App;
